import { HttpRequest } from '../stores/types/types';
import { appLogger } from '../utils/datadog';
import { buildSyncUrl, getAuthenticatedRequestInit, getSyncHeaders } from './utils';
import { Entity } from '../types/interfaces';
import { OnsiteLocation, PreviousLocation, SrtStream } from '../stores/reducers/video';

export const GetSrtStreamsForMeeting = async (
	token: string,
	meetingId: number,
): Promise<SrtStream[]> => {
	try {
		const response = await fetch(
			buildSyncUrl(`/v1/srt-streams/${meetingId}`),
			{
				method: HttpRequest.Get,
				headers: getSyncHeaders(token),
			},
		);

		if (response.ok) {
			return response.status === 200
				? response.json()
				: [];
		}

		appLogger.error(`GetSrtStreamsForMeeting error: ${response.status}`);
	} catch (error) {
		appLogger.error(`GetSrtStreamsForMeeting error: ${error}`);
	}

	return [];
};

export const CreateSrtStream = async (
	token: string,
	meetingId: number,
	name: string,
	safetyImage: string,
): Promise<Entity | null> => {
	try {
		const response = await fetch(
			buildSyncUrl(`/v1/srt-streams/${meetingId}`),
			{
				method: HttpRequest.Post,
				headers: getSyncHeaders(token),
				body: JSON.stringify({
					name,
					safetyImage,
				}),
			},
		);

		if (response.ok) {
			return response.json();
		}

		appLogger.error(`CreateSrtStream error: ${response.status}`);
	} catch (error) {
		appLogger.error(`CreateSrtStream error: ${error}`);
	}

	return null;
};

export const UpdateSrtStream = async (
	token: string,
	meetingId: number,
	streamId: number,
	updatedProps: Partial<SrtStream>,
): Promise<Entity | null> => {
	try {
		const response = await fetch(
			buildSyncUrl(`/v1/srt-streams/${meetingId}/${streamId}`),
			{
				method: HttpRequest.Put,
				headers: getSyncHeaders(token),
				body: JSON.stringify(updatedProps),
			},
		);

		if (response.ok) {
			return response.json();
		}

		appLogger.error(`UpdateSrtStream error: ${response.status}`);
	} catch (error) {
		appLogger.error(`UpdateSrtStream error: ${error}`);
	}

	return null;
};

export const DeleteSrtStream = async (
	token: string,
	meetingId: number,
	streamId: number,
): Promise<Entity | null> => {
	try {
		const response = await fetch(
			buildSyncUrl(`/v1/srt-streams/${meetingId}/${streamId}`),
			{
				method: HttpRequest.Delete,
				headers: getSyncHeaders(token),
			},
		);

		if (response.ok) {
			return response.json();
		}

		appLogger.error(`DeleteSrtStream error: ${response.status}`);
	} catch (error) {
		appLogger.error(`DeleteSrtStream error: ${error}`);
	}

	return null;
};

export const GetOnsiteLocationsForMeeting = async (
	token: string,
	meetingId: number,
): Promise<OnsiteLocation[]> => {
	try {
		const response = await fetch(
			buildSyncUrl(`/v1/room/${meetingId}/onsite-locations`),
			{
				method: HttpRequest.Get,
				headers: getSyncHeaders(token),
			},
		);

		if (response.ok) {
			return response.status === 200
				? response.json()
				: [];
		}

		appLogger.error(`GetOnsiteLocationsForMeeting error: ${response.status}`);
	} catch (error) {
		appLogger.error(`GetOnsiteLocationsForMeeting error: ${error}`);
	}

	return [];
};

export const CreateOnsiteLocation = async (
	token: string,
	meetingId: number,
	locationName: string | null,
	safetyImage: string | null,
	holdSlide: string | null,
): Promise<Entity | null> => {
	try {
		const response = await fetch(
			buildSyncUrl(`/v1/room/${meetingId}/onsite-locations`),
			{
				method: HttpRequest.Post,
				headers: getSyncHeaders(token),
				body: JSON.stringify({
					locationName,
					safetyImage,
					holdSlide,
				}),
			},
		);

		if (response.ok) {
			return response.json();
		}

		appLogger.error(`CreateOnsiteLocation error: ${response.status}`);
	} catch (error) {
		appLogger.error(`CreateOnsiteLocation error: ${error}`);
	}

	return null;
};

export const UpdateOnsiteLocation = async (
	token: string,
	meetingId: number,
	locationId: number,
	updatedProps: Partial<OnsiteLocation>,
): Promise<Entity | null> => {
	try {
		const response = await fetch(
			buildSyncUrl(`/v1/room/${meetingId}/onsite-locations/${locationId}`),
			{
				method: HttpRequest.Put,
				headers: getSyncHeaders(token),
				body: JSON.stringify(updatedProps),
			},
		);

		if (response.ok) {
			return response.json();
		}

		appLogger.error(`UpdateOnsiteLocation error: ${response.status}`);
	} catch (error) {
		appLogger.error(`UpdateOnsiteLocation error: ${error}`);
	}

	return null;
};

export const DeleteOnsiteLocation = async (
	token: string,
	meetingId: number,
	locationId: number,
): Promise<Entity | null> => {
	try {
		const response = await fetch(
			buildSyncUrl(`/v1/room/${meetingId}/onsite-locations/${locationId}`),
			{
				method: HttpRequest.Delete,
				headers: getSyncHeaders(token),
			},
		);

		if (response.ok) {
			return response.json();
		}

		appLogger.error(`DeleteOnsiteLocation error: ${response.status}`);
	} catch (error) {
		appLogger.error(`DeleteOnsiteLocation error: ${error}`);
	}

	return null;
};

export const GetPreviousLocationsForMeeting = async (
	token: string,
	meetingId: number,
): Promise<PreviousLocation[]> => {
	try {
		const response = await fetch(
			`${process.env.REACT_APP_API_HOST}/v3/greenroom/${meetingId}/previous-onsite-locations`,
			{
				method: HttpRequest.Get,
				...getAuthenticatedRequestInit(token),
			},
		);

		if (response.ok) {
			return response.status === 200
				? response.json()
				: [];
		}

		appLogger.error(`GetPreviousLocationsForMeeting error: ${response.status}`);
	} catch (error) {
		appLogger.error(`GetPreviousLocationsForMeeting error: ${error}`);
	}

	return [];
};

export const VerifyOnsiteLocationForMeeting = async (
	token: string,
	meetingSlug: string,
	pathName: string,
): Promise<number | null> => {
	try {
		const response = await fetch(
			`${process.env.REACT_APP_API_HOST}/v3/greenroom/${meetingSlug}/verify-onsite-location/${pathName}`,
			{
				method: HttpRequest.Get,
				...getAuthenticatedRequestInit(token),
			},
		);

		if (response.ok && response.status === 200) {
			const result = await response.json();
			if (result.id) {
				return result.id;
			}
		}

		appLogger.error(`VerifyOnsiteLocationForMeeting error: ${response.status}`);
	} catch (error) {
		appLogger.error(`VerifyOnsiteLocationForMeeting error: ${error}`);
	}

	return null;
};
